<template>
  <Modal name="attackWeapon">
    <template v-slot:default="{ options }">
      <ModalCard>
        <template v-slot:title>Attack With {{ options.weapon.name }}</template>
        
        <div class="field has-addons has-addons-centered">
          <div class="control">
            <DiceRoll ref="diceRoller" :roll="roll" :character="character" :computed="computed" />
          </div>
        </div>

        <div class="field is-grouped is-grouped-centered">
          <!-- <div class="control">
            <div class="buttons has-addons">
              <template v-if="options.weapon.tags.includes('oneHand')">
                <a :class="{ 'button': true }" :disabled="true">Main-hand</a>
                <a :class="{ 'button': true }" :disabled="true">Off-hand</a>
              </template>
              <a v-if="options.weapon.tags.includes('twoHand')" :class="{ 'button': true }" :disabled="true">Two-hands</a>
            </div>
          </div> -->
          <div v-if="options.weapon.tags.includes('melee') && options.weapon.tags.includes('ranged')" class="control">
            <div class="buttons has-addons">
              <a :class="{ 'button': true, 'is-primary': selectedRange === 'melee' }" @click="selectedRange = 'melee'">Melee</a>
              <a :class="{ 'button': true, 'is-primary': selectedRange === 'ranged' }" @click="selectedRange = 'ranged'">Ranged</a>
            </div>
          </div>
          <div class="field has-addons has-addons-centered">
            <div v-if="!appState.hardRolls" class="control">
              <a class="button is-primary" @click="rollDice">Roll</a>
            </div>
            <div v-if="bonuses.inspiration" class="control">
              <a :class="{ 'button': true, 'is-primary': useInspiration }" :disabled="options.removeInspiration" @click="useInspiration = !useInspiration">+1</a>
            </div>
          </div>
        </div>

        <template v-slot:buttons>
          <div class="button is-primary" @click="openModal('damageWeapon', { ...options, selectedRange })" :disabled="(!appState.hardRolls && !outcomeValue) ? true : null">
            Roll for Damage
          </div>
        </template>
      </ModalCard>
    </template>
  </Modal>
</template>

<script>
import { defineComponent, ref, watch, inject } from 'vue'

import useModal from '@/hooks/useModal'

import Modal from '@/components/Modal'
import ModalCard from '@/components/ModalCard'
import DiceRoll from '@/components/DiceRoll'

export default defineComponent({
  components: {
    DiceRoll,
    Modal,
    ModalCard
  },

  setup (props, { emit }) {
    const character = inject('character')
    const computed = inject('computedCharacter')
    const appState = inject('appState')

    const { currentModal, openModal } = useModal()

    const selectedRange = ref(null)
    const useInspiration = ref(false)
    watch(currentModal, (modal) => {
      if (modal === null || modal.name !== 'attackWeapon') {
        useInspiration.value = false
        return
      }

      if (modal.options.weapon.tags.includes('ranged')) {
        if (modal.options.weapon.tags.includes('melee')) {
          selectedRange.value = 'melee'
        } else {
          selectedRange.value = 'ranged'
        }
      } else {
        selectedRange.value = 'melee'
      }
    })

    const outcomeValue = ref(null)
    const diceRoller = ref(null)
    const rollDice = () => {
      if (useInspiration.value && computed.bonuses.value.inspiration) {
        currentModal.value.options.onClose = () => {
          emit('removeInspiration')
        }
      } else {
        delete currentModal.value.options.onClose
      }

      outcomeValue.value = diceRoller.value.rollDice().outcome
    }
    watch(currentModal, () => {
      outcomeValue.value = null
    })

    const roll = ref(null)
    const makeRoll = (modal, inspired, range) => {
      if (modal === null || modal.name !== 'attackWeapon') {
        return roll.value = []
      }

      outcomeValue.value = null
      if (diceRoller.value) diceRoller.value.reset()

      const parts = []
      const weapon = modal.options.weapon

      let ability
      if (weapon.finesse) {
        ability = [ 'str', 'dex' ]
          .map((key) => ({ key, value: computed.abilities.value[key] }))
          .sort((a, b) => b.value - a.value)[0]
          .key
      } else {
        if (range === 'ranged') ability = 'dex'
        else if (range === 'melee') ability = 'str'
      }
      parts.push(`+1d20`, `+${ability}`)

      const isProf = computed.mainHandWeaponChoices.value.includes(modal.options.key)
      if (isProf) parts.push(`+prof`)

      const bonus = range === 'melee' 
        ? computed.bonuses.value.meleeAttack
        : computed.bonuses.value.rangedAttack
      if (bonus > 0) parts.push(`+${bonus}`)
      else if (bonus < 0) parts.push(`-${bonus}`)

      if (inspired && computed.bonuses.value.inspiration) {
        parts.push('+1')
      }

      roll.value = parts
    }
    watch(selectedRange, (range) => makeRoll(currentModal.value, useInspiration.value, range))
    watch(currentModal, (modal) => makeRoll(modal, useInspiration.value, selectedRange.value))
    watch(useInspiration, (inspired) => makeRoll(currentModal.value, inspired, selectedRange.value))

    return {
      appState,
      character,
      computed,
      ...computed,
      roll,
      openModal,
      selectedRange,
      diceRoller,
      rollDice,
      outcomeValue,
      useInspiration
    }
  }
})
</script>
